import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import fedHolidays from '@18f/us-federal-holidays';
import moment from 'moment-timezone';
import 'react-datetime/css/react-datetime.css';
import DTCopyTextSendMacroView from '../DTCopyTextSendMacroView';
import { withConfirmation } from '../../../DTConfirmationView/withConfirmation';
import { Container, DateText, SelectedText } from './DTChangeShippingDateThenSendMacroNode.styles';
import CustomDateTimePicker from 'components/common/CustomDateTimePicker/CustomDateTimePicker';
import {
  getDecisionNodeAtPath,
  getSimplrAccountIdOfSelectedTicket,
  extractNonHistoricalTicketIdFromState,
} from 'extractFromState';
import { SubMessage } from '../DTCopyTextSendMacro.styles';
import { getSelectedOrderId, getSelectedOrderNumber, getSelectedSubscriptionsIds } from 'selectors/tools';
import { updateShipmentDate, updateSubscriptionDate } from 'api/orders';

const DTChangeShippingDateThenSendMacroNode = () => {
  const selectedSubscriptionsIdList = useSelector(getSelectedSubscriptionsIds);
  const [selectedDate, setSelectedDate] = useState(null);
  const simplrAccountId = useSelector(getSimplrAccountIdOfSelectedTicket);
  const orderId = useSelector(getSelectedOrderId);
  const orderNumber = useSelector(getSelectedOrderNumber);
  const conversationId = useSelector(extractNonHistoricalTicketIdFromState);
  const decisionNode = useSelector(getDecisionNodeAtPath);
  const isOrder = decisionNode.changeDateSubject !== 'subscription';
  let daysOutBlocked = parseInt(decisionNode.daysOutBlocked);

  if ((isNaN(daysOutBlocked) || daysOutBlocked < 2) && isOrder) {
    daysOutBlocked = 2;
  }

  const checkEligibleDate = useCallback(
    (current) => {
      const SATURDAY = 6;
      const SUNDAY = 0;
      const dayOfWeek = current.day();

      const holidays = fedHolidays
        .allForYear()
        .filter((holiday) => holiday.name !== 'Juneteenth National Independence Day');

      const isHoliday = holidays.some((holiday) => moment(holiday.date).isSame(current, 'date'));
      const isWeekend = dayOfWeek === SATURDAY || dayOfWeek === SUNDAY;

      const extraDaysToAddForWeekend = moment().day() + daysOutBlocked >= SATURDAY ? 2 : 0;
      const isFarEnough = current.isAfter(moment().add(daysOutBlocked + extraDaysToAddForWeekend, 'days'));

      return isFarEnough && !isWeekend && !isHoliday;
    },
    [daysOutBlocked]
  );

  const updateOrderDate = async () => {
    const params = {
      simplrAccountId,
      conversationId,
      orderId,
      orderNumber,
      shipmentDate: selectedDate.format('YYYY-MM-DD'),
    };
    const { message } = await updateShipmentDate(params);
    if (message !== 'success') {
      throw new Error({ message });
    }
  };

  const updateSubscriptionsDate = async () => {
    await Promise.all(
      selectedSubscriptionsIdList.map(async (subscriptionId) => {
        const params = {
          simplrAccountId,
          subscriptionId,
          newShipDate: selectedDate.format('YYYY-MM-DD'),
        };

        try {
          await updateSubscriptionDate(params);
        } catch (error) {
          throw new Error(`Error for subscriptionId: ${subscriptionId}`);
        }
      })
    );
  };

  const handleNextButtonClick = async () => {
    if (isOrder) {
      await updateOrderDate();
    } else {
      await updateSubscriptionsDate();
    }
  };

  return (
    <DTCopyTextSendMacroView
      buttonText={`NEXT`}
      onNextButtonClick={handleNextButtonClick}
      disableActionButton={!selectedDate}
      hideCopyText
      popupConfirmationText={`You are about to change the shipping date of this ${isOrder ? 'order' : 'subscription'}`}
      customErrorMessage={'Error updating the shipment date'}
    >
      <Container>
        <SubMessage>Please note that you cannot select weekends and holidays, as well as too close to today</SubMessage>
        {selectedDate && (
          <SelectedText>
            {isOrder ? 'Order' : 'Subscription'} shipping date:
            <DateText>{selectedDate.format('MM/DD/YY')}</DateText>
          </SelectedText>
        )}
        <CustomDateTimePicker
          value={selectedDate}
          input={false}
          isValidDate={checkEligibleDate}
          onChange={(date) => setSelectedDate(date)}
          timeFormat={false}
        />
      </Container>
    </DTCopyTextSendMacroView>
  );
};

export default withConfirmation(DTChangeShippingDateThenSendMacroNode);
